import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import useProducts from "@/hooks/useProducts";
import PageHero from "@/components/PageHero";
import ProductCard from "@/components/ProductCard";
import { FadeUp } from "@/components/Reveal";

export default function Products() {
  usePageMeta(
    "Products | Krithish Scientific Company",
    "Full catalog of scientific, industrial & lab equipments: autoclaves, hot air ovens, BOD incubators, water & oil baths, humidity chambers, deep freezers, furnaces, laminar air flow, balances, microscopes and more."
  );
  const { data: products, isLoading } = useProducts();
  const [query, setQuery] = useState("");

  const groups = useMemo(() => {
    const list = (products || []).filter((p) =>
      `${p.name} ${p.line_name} ${(p.applications || []).join(" ")}`.toLowerCase().includes(query.toLowerCase())
    );
    const byLine = new Map();
    for (const p of list) {
      if (!byLine.has(p.line)) byLine.set(p.line, { slug: p.line, name: p.line_name, items: [] });
      byLine.get(p.line).items.push(p);
    }
    return [...byLine.values()];
  }, [products, query]);

  const total = (products || []).length;

  return (
    <>
      <PageHero
        eyebrow={`Product Catalog // ${total || "…"} Products`}
        title="Scientific, industrial & lab equipments."
        sub="Manufactured and supplied under the 'Krithish' brand — every product photo below is from our own printed catalog. Select a product for details, ordering and price enquiry."
      />

      <section className="bg-slate-50 py-20 lg:py-24" data-testid="products-grid">
        <div className="mx-auto max-w-7xl px-6">
          <FadeUp>
            <div className="sticky top-[104px] z-30 lg:top-[112px]">
              <div className="relative max-w-xl rounded-xl bg-slate-50/95 py-2 backdrop-blur">
                <Search size={17} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" style={{ marginTop: 8 }} />
                <input
                  data-testid="products-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search products… e.g. oven, incubator, bath, balance"
                  className="w-full rounded-xl border border-slate-200 bg-white py-3.5 pl-11 pr-4 text-sm outline-none transition focus:border-rose-400 focus:ring-2 focus:ring-rose-100"
                />
              </div>
            </div>
          </FadeUp>

          {isLoading ? (
            <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="h-72 animate-pulse rounded-2xl bg-slate-200/60" />
              ))}
            </div>
          ) : groups.length === 0 ? (
            <div className="mt-12 rounded-2xl border border-dashed border-slate-300 bg-white p-14 text-center">
              <p className="font-display text-lg font-bold text-slate-900">No products match "{query}"</p>
              <p className="mt-2 text-sm text-slate-500">Try another keyword — or call us; we also build custom units.</p>
            </div>
          ) : (
            <div className="mt-10 space-y-16">
              {groups.map((g, gi) => (
                <div key={g.slug} id={g.slug} data-testid={`line-${g.slug}`} className="scroll-mt-40">
                  <FadeUp>
                    <div className="mb-6 flex items-baseline justify-between gap-4 border-b-2 border-navy pb-3">
                      <h2 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">
                        <span className="mr-3 font-mono text-sm font-semibold text-safety">{String(gi + 1).padStart(2, "0")}</span>
                        {g.name}
                      </h2>
                      <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">
                        {g.items.length} product{g.items.length > 1 ? "s" : ""}
                      </span>
                    </div>
                  </FadeUp>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                    {g.items.map((p, i) => (
                      <FadeUp key={p.slug} delay={Math.min(i * 0.04, 0.24)} className="h-full">
                        <ProductCard p={p} index={p.sort_order ? p.sort_order - 1 : i} />
                      </FadeUp>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
